(() => {
  const STORAGE = {
    start: 'alib-rental-start',
    end: 'alib-rental-end',
    discount: 'alib-discount-code'
  };

  const root = (() => {
    const value = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return value.endsWith('/') ? value : value + '/';
  })();

  const qs = (selector, parent = document) => parent.querySelector(selector);
  const qsa = (selector, parent = document) => Array.from(parent.querySelectorAll(selector));

  let cartState = null;
  let queue = Promise.resolve();

  const enqueue = (task) => {
    queue = queue.catch(() => {}).then(task);
    return queue;
  };

  const money = (cents) =>
    `${(Math.max(0, Number(cents || 0)) / 100).toLocaleString('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} €`;

  const readLocal = (key) => {
    try { return localStorage.getItem(key) || ''; } catch (_) { return ''; }
  };

  const writeLocal = (key, value) => {
    try {
      if (value) localStorage.setItem(key, value);
      else localStorage.removeItem(key);
    } catch (_) {}
  };

  async function requestCart(path, options = {}) {
    const response = await fetch(root + path, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {})
      },
      ...options
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || `Eroare coș (${response.status})`);
    }
    return data;
  }

  const fetchCart = () => requestCart('cart.js');

  function updateBadge(count) {
    qsa('.pana2-cart span').forEach((badge) => {
      badge.textContent = String(Number(count || 0));
    });
  }

  function getDates() {
    return {
      start: qs('[data-rental-start]')?.value || '',
      end: qs('[data-rental-end]')?.value || ''
    };
  }

  function saveDatesLocal() {
    const { start, end } = getDates();
    writeLocal(STORAGE.start, start);
    writeLocal(STORAGE.end, end);
    return { start, end };
  }

  function rentalDays() {
    const { start, end } = getDates();
    if (!start || !end) return 1;

    const startDate = new Date(start + 'T00:00:00');
    const endDate = new Date(end + 'T00:00:00');
    const diff = Math.ceil((endDate - startDate) / 86400000);
    return Math.max(1, Number.isFinite(diff) ? diff : 1);
  }

  function currentSubtotal() {
    const days = rentalDays();
    let subtotal = 0;

    qsa('[data-cart-line]').forEach((line) => {
      const unit = Math.max(0, Number(line.dataset.unitPrice || 0));
      const qty = Math.max(0, Number(qs('[data-cart-qty]', line)?.value || 0));
      const lineTotal = unit * qty * days;
      subtotal += lineTotal;

      const price = qs('[data-line-price]', line);
      if (price) price.textContent = money(lineTotal);
    });

    return subtotal;
  }

  function scaledDiscount(subtotal, cart) {
    if (!cart || subtotal <= 0) return 0;

    const original = Math.max(0, Number(cart.original_total_price || 0));
    const final = Math.max(0, Number(cart.total_price || 0));

    if (original > 0 && final < original) {
      const ratio = Math.max(0, Math.min(1, final / original));
      return Math.max(0, subtotal - Math.round(subtotal * ratio));
    }
    return 0;
  }

  function updateTotals(cart = cartState) {
    const subtotal = currentSubtotal();
    const discount = scaledDiscount(subtotal, cart);
    const total = Math.max(0, subtotal - discount);

    const subtotalEl = qs('[data-subtotal]');
    const discountRow = qs('[data-discount-row]');
    const discountAmount = qs('[data-discount-amount]');
    const noVat = qs('[data-total-after-discount]');
    const withVat = qs('[data-total-with-vat]');
    const daysRow = qs('[data-rental-days-row]');
    const daysEl = qs('[data-rental-days]');
    const totalsBox = qs('[data-cart-total-cents]');

    if (subtotalEl) subtotalEl.textContent = money(subtotal);
    if (discountRow) discountRow.hidden = discount <= 0;
    if (discountAmount) discountAmount.textContent = '- ' + money(discount);
    if (noVat) noVat.textContent = money(total);
    if (withVat) withVat.textContent = money(Math.round(total * 1.21));
    if (daysRow) daysRow.hidden = false;
    if (daysEl) daysEl.textContent = String(rentalDays());

    if (totalsBox && cart) {
      totalsBox.dataset.cartTotalCents = String(cart.total_price || 0);
      totalsBox.dataset.cartOriginalTotalCents = String(cart.original_total_price || cart.total_price || 0);
      totalsBox.dataset.cartTotalDiscountCents = String(cart.total_discount || 0);
    }
  }

  function syncDomWithCart(cart) {
    const lines = qsa('[data-cart-line]');
    const items = Array.isArray(cart?.items) ? cart.items : [];

    lines.forEach((line, index) => {
      const item = items[index];
      if (!item) return;

      line.dataset.line = String(index + 1);
      line.dataset.lineKey = String(item.key || '');

      if (Number.isFinite(Number(item.original_price))) {
        line.dataset.unitPrice = String(item.original_price);
      }

      const qty = qs('[data-cart-qty]', line);
      if (qty && document.activeElement !== qty) {
        qty.value = String(item.quantity || 0);
      }
    });
  }

  function renderEmptyCart() {
    const section = qs('.section .container.narrow');
    if (!section) return;

    qsa('.cart-lines, .missing-product-note, .quote-box', section).forEach((el) => el.remove());

    if (!qs('[data-cart-empty-state]', section)) {
      const empty = document.createElement('div');
      empty.dataset.cartEmptyState = '';
      empty.innerHTML = '<p>Coşul este gol.</p><a class="btn" href="' + root + 'collections">Vezi catalogul</a>';
      section.appendChild(empty);
    }
  }

  function applyState(cart) {
    cartState = cart;
    updateBadge(cart?.item_count || 0);
    syncDomWithCart(cart);

    if ((cart?.item_count || 0) === 0) {
      renderEmptyCart();
      return;
    }

    updateTotals(cart);
  }

  function restoreDates(cart) {
    const startInput = qs('[data-rental-start]');
    const endInput = qs('[data-rental-end]');
    if (!startInput || !endInput) return;

    const attrs = cart?.attributes || {};
    const start = startInput.value || attrs._rental_start || readLocal(STORAGE.start);
    const end = endInput.value || attrs._rental_end || readLocal(STORAGE.end);

    if (start) startInput.value = start;
    if (end) endInput.value = end;

    writeLocal(STORAGE.start, startInput.value || '');
    writeLocal(STORAGE.end, endInput.value || '');
    updateTotals(cart);
  }

  async function persistDates() {
    if (!cartState) cartState = await fetchCart();

    const { start, end } = saveDatesLocal();
    const attributes = {
      ...(cartState.attributes || {}),
      _rental_start: start,
      _rental_end: end
    };

    const cart = await requestCart('cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ attributes })
    });

    applyState(cart);
    return cart;
  }

  async function applyDiscount(code) {
    if (!cartState) cartState = await fetchCart();

    const normalized = String(code || '').trim().toUpperCase();
    const { start, end } = saveDatesLocal();

    writeLocal(STORAGE.discount, normalized);

    const cart = await requestCart('cart/update.js', {
      method: 'POST',
      body: JSON.stringify({
        discount: normalized,
        attributes: {
          ...(cartState.attributes || {}),
          _rental_start: start,
          _rental_end: end
        }
      })
    });

    applyState(cart);

    const input = qs('[data-shopify-discount-input]');
    const message = qs('[data-shopify-discount-message]');

    const applied =
      Number(cart.total_discount || 0) > 0 ||
      (Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0) ||
      (Array.isArray(cart.discount_applications) && cart.discount_applications.length > 0);

    if (!normalized) {
      if (input) input.value = '';
      if (message) message.textContent = 'Reducerea a fost eliminată.';
      return;
    }

    if (applied) {
      if (input) input.value = normalized;
      if (message) message.textContent = 'Cod aplicat: ' + normalized;
    } else {
      writeLocal(STORAGE.discount, '');
      if (message) message.textContent = 'Codul ' + normalized + ' nu este valid pentru acest coș.';
    }
  }

  async function changeLine(lineElement, quantity, allowRetry = true) {
    if (!cartState) {
      cartState = await fetchCart();
      syncDomWithCart(cartState);
    }

    let key = lineElement?.dataset.lineKey || '';

    if (!key) {
      const index = qsa('[data-cart-line]').indexOf(lineElement);
      key = String(cartState?.items?.[index]?.key || '');
    }

    if (!key) throw new Error('Produsul nu a putut fi identificat în coș.');

    try {
      const cart = await requestCart('cart/change.js', {
        method: 'POST',
        body: JSON.stringify({
          id: key,
          quantity: Math.max(0, Number(quantity || 0))
        })
      });

      if (Number(quantity) <= 0 && lineElement) lineElement.remove();
      applyState(cart);
      return cart;
    } catch (error) {
      if (!allowRetry) throw error;
      const fresh = await fetchCart();
      cartState = fresh;
      syncDomWithCart(fresh);
      return changeLine(lineElement, quantity, false);
    }
  }

  async function updateQuantity(line, quantity) {
    if (!line) return;

    const input = qs('[data-cart-qty]', line);
    const clean = Math.max(0, Number.parseInt(quantity, 10) || 0);

    if (input) input.value = String(clean);
    updateTotals();

    qsa('button, input', line).forEach((el) => { el.disabled = true; });

    try {
      await changeLine(line, clean);
    } finally {
      if (document.contains(line)) {
        qsa('button, input', line).forEach((el) => { el.disabled = false; });
      }
    }
  }

  async function addProductForm(form) {
    const button = qs('button[type="submit"]', form);
    const originalText = button?.dataset.addText || button?.textContent || 'Adaugă în coș';

    if (button) {
      button.disabled = true;
      button.textContent = 'Se adaugă...';
    }

    try {
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Produsul nu a putut fi adăugat.');
      }

      const cart = await fetchCart();
      cartState = cart;
      updateBadge(cart.item_count || 0);

      if (button) {
        button.textContent = 'Adăugat ✓';
        setTimeout(() => {
          button.disabled = false;
          button.textContent = originalText;
        }, 900);
      }
    } catch (error) {
      console.error('AliB add to cart:', error);
      if (button) {
        button.textContent = 'Încearcă din nou';
        setTimeout(() => {
          button.disabled = false;
          button.textContent = originalText;
        }, 1200);
      }
    }
  }

  function bindEvents() {
    document.addEventListener('submit', (event) => {
      const form = event.target;

      if (form.matches('[data-ajax-cart-add]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        enqueue(() => addProductForm(form)).catch(console.error);
        return;
      }

      if (form.matches('[data-cart-form], [data-shopify-discount-form]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    document.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        const line = remove.closest('[data-cart-line]');
        if (!line) return;

        const oldText = remove.textContent;
        remove.disabled = true;
        remove.textContent = 'Se șterge...';

        enqueue(() => changeLine(line, 0))
          .catch((error) => {
            if (document.contains(remove)) {
              remove.disabled = false;
              remove.textContent = oldText;
            }
            console.error(error);
          });
        return;
      }

      const apply = event.target.closest('[data-discount-apply]');
      if (apply) {
        event.preventDefault();
        const input = qs('[data-shopify-discount-input]');
        const oldText = apply.textContent;

        apply.disabled = true;
        apply.textContent = 'Se aplică...';

        enqueue(() => applyDiscount(input?.value || ''))
          .catch((error) => {
            const message = qs('[data-shopify-discount-message]');
            if (message) message.textContent = error.message || 'Codul nu a putut fi aplicat.';
          })
          .finally(() => {
            apply.disabled = false;
            apply.textContent = oldText;
          });
      }
    }, true);

    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-cart-qty]')) {
        const line = event.target.closest('[data-cart-line]');
        enqueue(() => updateQuantity(line, event.target.value)).catch(console.error);
        return;
      }

      if (event.target.matches('[data-rental-start], [data-rental-end]')) {
        saveDatesLocal();
        updateTotals();
        enqueue(persistDates).catch(console.error);
      }
    });

    document.addEventListener('input', (event) => {
      if (event.target.matches('[data-rental-start], [data-rental-end]')) {
        saveDatesLocal();
        updateTotals();
      } else if (event.target.matches('[data-cart-qty]')) {
        updateTotals();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.target.matches('[data-shopify-discount-input]') && event.key === 'Enter') {
        event.preventDefault();
        qs('[data-discount-apply]')?.click();
      }
    });
  }

  async function init() {
    bindEvents();

    try {
      const cart = await fetchCart();
      cartState = cart;
      restoreDates(cart);
      applyState(cart);

      const savedDiscount = readLocal(STORAGE.discount);
      const discountInput = qs('[data-shopify-discount-input]');
      if (discountInput && !discountInput.value && savedDiscount) {
        discountInput.value = savedDiscount;
      }
    } catch (error) {
      console.error('AliB cart init:', error);
      updateTotals();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('pageshow', () => {
    fetchCart()
      .then((cart) => {
        cartState = cart;
        restoreDates(cart);
        applyState(cart);
      })
      .catch(() => {});
  });
})();
